class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        setTimeout(() => {
          if (typeof window.initEmptyCartCarousel === 'function') {
            window.initEmptyCartCarousel();
          }
        });
      });
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (typeof window.initEmptyCartCarousel === 'function') window.initEmptyCartCarousel();
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
    }
    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    setTimeout(() => {
      document.body.classList.remove('overflow-hidden');
      document.documentElement.style.setProperty('--scrollbar-width', '0px');
    }, 450);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ──────────────────────────────────────────────────────────────────────────
   Custom Empty Cart Carousel & Size Selector Functions
   ────────────────────────────────────────────────────────────────────────── */

window.initEmptyCartCarousel = function() {
  bindCarousel('EmptyCartCarouselItems', 'EmptyCartCarouselDots');
  bindCarousel('ActiveCartCarouselItems', 'ActiveCartCarouselDots');

  function bindCarousel(itemsId, dotsId) {
    const carouselItems = document.getElementById(itemsId);
    const dotsContainer = document.getElementById(dotsId);
    if (carouselItems && dotsContainer) {
      if (carouselItems.dataset.carouselBound === 'true') return;
      carouselItems.dataset.carouselBound = 'true';

      const dots = dotsContainer.querySelectorAll('.empty-cart-carousel-dot');
      
      // Bind scroll listener
      carouselItems.addEventListener('scroll', () => {
        const firstItem = carouselItems.querySelector('.empty-cart-carousel__item');
        if (!firstItem) return;
        const itemWidth = firstItem.offsetWidth;
        const gap = 16; // 1.6rem gap
        const index = Math.round(carouselItems.scrollLeft / (itemWidth + gap));
        dots.forEach((dot, idx) => {
          if (idx === index) {
            dot.classList.add('active');
          } else {
            dot.classList.remove('active');
          }
        });
      });

      // Bind dots click listener
      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.getAttribute('data-index'));
          const firstItem = carouselItems.querySelector('.empty-cart-carousel__item');
          if (!firstItem) return;
          const itemWidth = firstItem.offsetWidth;
          const gap = 16;
          carouselItems.scrollTo({
            left: idx * (itemWidth + gap),
            behavior: 'smooth'
          });
        });
      });
    }
  }
};

window.openEmptyCartSizeSelector = function(element) {
  try {
    const hasSizes = element.getAttribute('data-has-sizes') === 'true';
    const variants = JSON.parse(element.getAttribute('data-variants-json') || '[]');
    const sizes = JSON.parse(element.getAttribute('data-sizes-json') || '[]');

    window.currentCartSelectorProductId = element.getAttribute('data-product-id');
    window.currentCartSelectorVariants = variants;
    window.currentCartSelectorAddBtnElement = element;
    window.currentCartSelectorSelectedVariantId = null;

    if (!hasSizes || sizes.length === 0) {
      // Add directly to cart if no sizes option
      const defaultVariantId = variants[0]?.id;
      if (defaultVariantId) {
        addBestsellerToCart(defaultVariantId, element);
      }
      return;
    }

    // Populate sizes in the bottom sheet selector
    const optionsWrapper = document.getElementById('CartDrawerSizeSelectorOptions');
    const errorEl = document.getElementById('CartDrawerSizeSelectorError');
    const doneBtn = document.getElementById('CartDrawerSizeSelectorDoneBtn');
    
    if (errorEl) errorEl.style.display = 'none';
    if (doneBtn) {
      doneBtn.disabled = false;
      doneBtn.innerHTML = '<span>DONE</span>';
    }

    if (optionsWrapper) {
      optionsWrapper.innerHTML = '';
      sizes.forEach(sz => {
        const optionBtn = document.createElement('div');
        optionBtn.className = `cart-drawer-size-selector-option${!sz.available ? ' disabled' : ''}`;
        optionBtn.textContent = sz.value;
        optionBtn.setAttribute('data-variant-id', sz.id);

        if (sz.available) {
          optionBtn.addEventListener('click', () => {
            optionsWrapper.querySelectorAll('.cart-drawer-size-selector-option').forEach(btn => btn.classList.remove('active'));
            optionBtn.classList.add('active');
            window.currentCartSelectorSelectedVariantId = sz.id;
            if (errorEl) errorEl.style.display = 'none';
          });
        }
        optionsWrapper.appendChild(optionBtn);
      });
    }

    // Open size selector
    const overlay = document.getElementById('CartDrawerSizeSelectorOverlay');
    const selector = document.getElementById('CartDrawerSizeSelector');
    if (overlay) overlay.classList.add('active');
    if (selector) selector.classList.add('active');
  } catch (err) {
    console.error('Error parsing product variants or sizes:', err);
  }
};

window.closeCartDrawerSizeSelector = function() {
  const overlay = document.getElementById('CartDrawerSizeSelectorOverlay');
  const selector = document.getElementById('CartDrawerSizeSelector');
  if (overlay) overlay.classList.remove('active');
  if (selector) selector.classList.remove('active');
};

window.submitCartDrawerSizeSelector = function() {
  const errorEl = document.getElementById('CartDrawerSizeSelectorError');
  if (!window.currentCartSelectorSelectedVariantId) {
    if (errorEl) errorEl.style.display = 'block';
    return;
  }

  const doneBtn = document.getElementById('CartDrawerSizeSelectorDoneBtn');
  if (doneBtn) {
    doneBtn.disabled = true;
    doneBtn.innerHTML = `
      <div class="loading__spinner" style="width: 20px; height: 20px; display: inline-flex; align-items: center; justify-content: center;">
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" style="width: 20px; height: 20px; color: #fff;">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30" stroke="currentColor"></circle>
        </svg>
      </div>
    `;
  }

  addBestsellerToCart(window.currentCartSelectorSelectedVariantId, window.currentCartSelectorAddBtnElement, doneBtn);
};

function addBestsellerToCart(variantId, buttonEl, doneBtnEl) {
  if (buttonEl) {
    buttonEl.style.pointerEvents = 'none';
    buttonEl.style.opacity = '0.5';
    buttonEl.innerText = 'ADDING...';
  }

  const cartDrawer = document.querySelector('cart-drawer');
  const sections = cartDrawer ? cartDrawer.getSectionsToRender().map((s) => s.id) : [];

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', 1);
  if (sections.length > 0) {
    formData.append('sections', sections.join(','));
    formData.append('sections_url', window.routes?.cart_url || '/cart');
  }

  const baseAddUrl = window.routes?.cart_add_url || '/cart/add';
  const fetchUrl = baseAddUrl.endsWith('.js') ? baseAddUrl : `${baseAddUrl}.js`;

  fetch(fetchUrl, {
    method: 'POST',
    headers: {
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: formData
  })
  .then(response => response.json())
  .then(response => {
    if (response.status) {
      alert(response.description || response.message || 'Could not add variant to cart.');
      return;
    }

    // Close the size selector
    window.closeCartDrawerSizeSelector();

    // Fire cartUpdate event so cart drawer updates automatically
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form',
        productVariantId: variantId,
        cartData: response,
      });
    }

    if (cartDrawer) {
      if (cartDrawer.classList.contains('is-empty')) {
        cartDrawer.classList.remove('is-empty');
      }
      cartDrawer.renderContents(response);
    }
  })
  .catch(err => {
    console.error('Error adding variant to cart:', err);
    alert('Failed to add item to cart. Please try again.');
  })
  .finally(() => {
    if (buttonEl) {
      buttonEl.style.pointerEvents = '';
      buttonEl.style.opacity = '';
      buttonEl.innerText = 'ADD TO CART';
    }
    if (doneBtnEl) {
      doneBtnEl.disabled = false;
      doneBtnEl.innerHTML = '<span>DONE</span>';
    }
  });
}
